import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import './ProviderAds.css';

const DEPORTE_OPTIONS = [
  { value: 'futbol', label: 'Fútbol' },
  { value: 'basquetbol', label: 'Básquetbol' },
  { value: 'voleibol', label: 'Voleibol' },
  { value: 'natacion', label: 'Natación' },
  { value: 'gimnasia', label: 'Gimnasia' },
  { value: 'tenis', label: 'Tenis' },
  { value: 'artes_marciales', label: 'Artes marciales' },
  { value: 'atletismo', label: 'Atletismo' },
  { value: 'general', label: 'General' },
];

const ESTADO_LABELS = {
  borrador: 'Borrador',
  programado: 'Programado',
  publicado: 'Publicado',
  vencido: 'Vencido',
};

const BANNER_MIN_WIDTH = 480;
const BANNER_MIN_HEIGHT = 120;
const BANNER_RECOMMENDED_WIDTH = 563;
const BANNER_RECOMMENDED_HEIGHT = 280;

function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo leer la imagen'));
    };
    img.src = url;
  });
}

function EstadoBadge({ estado }) {
  const label = ESTADO_LABELS[estado] || estado;
  return (
    <span className={`estado-pill estado-pill--${estado}`}>
      <span className="estado-pill__dot" aria-hidden="true" />
      {label}
    </span>
  );
}

const emptyForm = () => ({
  proveedor_name: '',
  deporte_objetivo: 'general',
  deportes_objetivo: ['general'],
  url_imagen: '',
  url_destino: '',
  fecha_inicio_date: todayDateInput(),
  fecha_inicio_time: '00:00',
  fecha_fin_date: '',
  fecha_fin_time: '23:59',
});

const LIMA_TIME_ZONE = 'America/Lima';

function parseApiDate(isoString) {
  if (!isoString) return null;
  const raw = String(isoString);
  const hasZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
  const date = new Date(hasZone ? raw : `${raw}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function limaParts(isoString) {
  const date = parseApiDate(isoString);
  if (!date) return null;
  const parts = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone: LIMA_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date).forEach((part) => {
    parts[part.type] = part.value;
  });
  if (parts.hour === '24') parts.hour = '00';
  return parts;
}

function todayDateInput() {
  return toDateInputValue(new Date().toISOString());
}

function toDateInputValue(isoString) {
  const parts = limaParts(isoString);
  if (!parts) return '';
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function toTimeInputValue(isoString) {
  const parts = limaParts(isoString);
  if (!parts) return '23:59';
  return `${parts.hour}:${parts.minute}`;
}

function toIsoFromDateAndTime(dateValue, timeValue) {
  if (!dateValue) return null;
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeValue || '00:00');
  if (!dateMatch || !timeMatch) return null;
  const utc = new Date(Date.UTC(
    Number(dateMatch[1]),
    Number(dateMatch[2]) - 1,
    Number(dateMatch[3]),
    Number(timeMatch[1]) + 5,
    Number(timeMatch[2]),
    0
  ));
  return Number.isNaN(utc.getTime()) ? null : utc.toISOString();
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

function parseTimeValue(timeValue) {
  const [hour = '23', minute = '59'] = (timeValue || '23:59').split(':');
  return {
    hour: hour.padStart(2, '0').slice(0, 2),
    minute: minute.padStart(2, '0').slice(0, 2),
  };
}

function buildTimeValue(hour, minute) {
  return `${hour}:${minute}`;
}

function formatDate(isoString) {
  const date = parseApiDate(isoString);
  if (!date) return '—';
  return new Intl.DateTimeFormat('es-PE', {
    timeZone: LIMA_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date);
}

const ProviderAds = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [uploading, setUploading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const { data: ads, isLoading } = useQuery(
    'provider-ads',
    async () => {
      const response = await api.get('/provider-ads');
      return response.data;
    }
  );

  const invalidate = () => queryClient.invalidateQueries('provider-ads');

  const createMutation = useMutation(
    (data) => api.post('/provider-ads', data),
    {
      onSuccess: () => {
        toast.success('Anuncio creado como borrador');
        invalidate();
        closeModal();
      },
      onError: (err) => toast.error(err.response?.data?.detail || 'Error al crear anuncio'),
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => api.put(`/provider-ads/${id}`, data),
    {
      onSuccess: () => {
        toast.success('Anuncio actualizado');
        invalidate();
        closeModal();
      },
      onError: (err) => toast.error(err.response?.data?.detail || 'Error al actualizar anuncio'),
    }
  );

  const publishMutation = useMutation(
    (id) => api.post(`/provider-ads/${id}/publish`),
    {
      onSuccess: () => {
        toast.success('Anuncio publicado');
        invalidate();
      },
      onError: (err) => toast.error(err.response?.data?.detail || 'No se pudo publicar el anuncio'),
    }
  );

  const unpublishMutation = useMutation(
    (id) => api.post(`/provider-ads/${id}/unpublish`),
    {
      onSuccess: () => {
        toast.success('Anuncio despublicado');
        invalidate();
      },
      onError: (err) => toast.error(err.response?.data?.detail || 'Error al despublicar'),
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/provider-ads/${id}`),
    {
      onSuccess: () => {
        toast.success('Anuncio eliminado');
        invalidate();
      },
      onError: (err) => toast.error(err.response?.data?.detail || 'Error al eliminar anuncio'),
    }
  );

  const closeModal = () => {
    setShowModal(false);
    setEditingAd(null);
    setFormData(emptyForm());
  };

  const openCreate = () => {
    setEditingAd(null);
    setFormData({
      ...emptyForm(),
      proveedor_name: user?.full_name || '',
    });
    setShowModal(true);
  };

  const openEdit = (ad) => {
    setEditingAd(ad);
    setFormData({
      proveedor_name: ad.proveedor_name,
      deporte_objetivo: ad.deporte_objetivo,
      deportes_objetivo: ad.deportes_objetivo?.length ? ad.deportes_objetivo : [ad.deporte_objetivo || 'general'],
      url_imagen: ad.url_imagen,
      url_destino: ad.url_destino,
      fecha_inicio_date: toDateInputValue(ad.fecha_inicio) || todayDateInput(),
      fecha_inicio_time: ad.fecha_inicio ? toTimeInputValue(ad.fecha_inicio) : '00:00',
      fecha_fin_date: toDateInputValue(ad.fecha_fin),
      fecha_fin_time: toTimeInputValue(ad.fecha_fin),
    });
    setShowModal(true);
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const { width, height } = await readImageDimensions(file);
      if (width < BANNER_MIN_WIDTH || height < BANNER_MIN_HEIGHT) {
        toast.error(
          `El banner debe medir al menos ${BANNER_MIN_WIDTH}×${BANNER_MIN_HEIGHT} px (tu imagen: ${width}×${height} px).`
        );
        event.target.value = '';
        return;
      }
    } catch {
      toast.error('No se pudo validar el tamaño de la imagen');
      event.target.value = '';
      return;
    }

    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    try {
      const response = await api.post('/provider-ads/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData((prev) => ({ ...prev, url_imagen: response.data.url_imagen }));
      toast.success('Imagen subida correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir imagen');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const fechaInicioIso = toIsoFromDateAndTime(formData.fecha_inicio_date, formData.fecha_inicio_time || '00:00');
    const fechaFinIso = toIsoFromDateAndTime(formData.fecha_fin_date, formData.fecha_fin_time);
    if (!fechaInicioIso) {
      toast.error('Indica una fecha de inicio válida');
      return;
    }
    if (!fechaFinIso) {
      toast.error('Indica una fecha de fin válida');
      return;
    }
    if (new Date(fechaInicioIso) >= new Date(fechaFinIso)) {
      toast.error('La fecha de inicio debe ser anterior a la fecha de fin');
      return;
    }
    if (!formData.url_imagen) {
      toast.error('Sube una imagen para el banner');
      return;
    }

    const deportes = (formData.deportes_objetivo || []).filter(Boolean);
    if (!deportes.length) {
      toast.error('Selecciona al menos un público objetivo');
      return;
    }

    const payload = {
      proveedor_name: formData.proveedor_name.trim(),
      deporte_objetivo: deportes[0],
      deportes_objetivo: deportes,
      url_imagen: formData.url_imagen,
      url_destino: formData.url_destino.trim(),
      fecha_inicio: fechaInicioIso,
      fecha_fin: fechaFinIso,
    };

    if (editingAd) {
      updateMutation.mutate({ id: editingAd.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const adDeportes = (ad) => (
    ad.deportes_objetivo?.length ? ad.deportes_objetivo : [ad.deporte_objetivo || 'general']
  );

  const getDeporteLabel = (slug) =>
    DEPORTE_OPTIONS.find((d) => d.value === slug)?.label || slug.replace(/_/g, ' ');

  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  const counts = (ads || []).reduce(
    (acc, ad) => {
      acc.total += 1;
      if (acc[ad.estado] !== undefined) acc[ad.estado] += 1;
      return acc;
    },
    { total: 0, borrador: 0, programado: 0, publicado: 0, vencido: 0 }
  );

  return (
    <div className="provider-ads-page page-container">
      <div className="provider-ads-toolbar">
        <div className="provider-ads-toolbar__intro">
          <p>
            Crea borradores, elige uno o varios deportes y publícalos cuando estés listo. Un anuncio publicado también se puede editar. Si la fecha de inicio es posterior a hoy, queda programado.
          </p>
        </div>
        <div className="provider-ads-toolbar__actions">
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            + Nuevo anuncio
          </button>
        </div>
      </div>

      {!isLoading && ads?.length > 0 && (
        <div className="provider-ads-stats">
          <span className="provider-ads-stat provider-ads-stat--total">
            <span className="provider-ads-stat__dot" />
            {counts.total} total
          </span>
          <span className="provider-ads-stat provider-ads-stat--borrador">
            <span className="provider-ads-stat__dot" />
            {counts.borrador} borrador{counts.borrador !== 1 ? 'es' : ''}
          </span>
          {counts.programado > 0 && (
            <span className="provider-ads-stat provider-ads-stat--programado">
              <span className="provider-ads-stat__dot" />
              {counts.programado} programado{counts.programado !== 1 ? 's' : ''}
            </span>
          )}
          <span className="provider-ads-stat provider-ads-stat--publicado">
            <span className="provider-ads-stat__dot" />
            {counts.publicado} publicado{counts.publicado !== 1 ? 's' : ''}
          </span>
          {counts.vencido > 0 && (
            <span className="provider-ads-stat provider-ads-stat--vencido">
              <span className="provider-ads-stat__dot" />
              {counts.vencido} vencido{counts.vencido !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="provider-ads-loading">Cargando anuncios…</div>
      ) : !ads?.length ? (
        <div className="provider-ads-empty">
          <div className="provider-ads-empty__icon" aria-hidden="true">📢</div>
          <h3>Sin anuncios todavía</h3>
          <p>Crea tu primer borrador, sube un banner y publícalo para aparecer en academias del deporte elegido.</p>
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            Crear primer anuncio
          </button>
        </div>
      ) : (
        <div className="provider-ads-card">
          <div className="provider-ads-table-wrap">
            <table className="provider-ads-table">
              <thead>
                <tr>
                  <th>Público objetivo</th>
                  <th>Nombre comercial</th>
                  <th>Desde</th>
                  <th>Vigencia hasta</th>
                  <th className="col-estado">Estado</th>
                  <th className="col-banner">Banner</th>
                  <th className="col-acciones">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {ads.map((ad) => (
                  <tr key={ad.id}>
                    <td>
                      <div className="publico-list">
                        {adDeportes(ad).map((slug) => (
                          <span key={slug} className="deporte-tag">{getDeporteLabel(slug)}</span>
                        ))}
                      </div>
                    </td>
                    <td className="col-nombre">{ad.proveedor_name}</td>
                    <td className="col-fecha">{formatDate(ad.fecha_inicio)}</td>
                    <td className="col-fecha">{formatDate(ad.fecha_fin)}</td>
                    <td className="col-estado">
                      <EstadoBadge estado={ad.estado} />
                    </td>
                    <td className="col-banner">
                      <div className="ad-thumb-wrap">
                        {ad.url_imagen ? (
                          <img src={ad.url_imagen} alt={`Banner ${ad.proveedor_name}`} className="ad-thumb" />
                        ) : (
                          <span className="ad-thumb-placeholder">Sin imagen</span>
                        )}
                      </div>
                    </td>
                    <td className="col-acciones">
                      <div className="row-actions">
                        <button
                          type="button"
                          className="btn-action btn-action--edit"
                          onClick={() => openEdit(ad)}
                        >
                          Editar
                        </button>
                        {ad.estado === 'borrador' && (
                          <button
                            type="button"
                            className="btn-action btn-action--publish"
                            onClick={() => publishMutation.mutate(ad.id)}
                            disabled={publishMutation.isLoading}
                          >
                            Publicar
                          </button>
                        )}
                        {(ad.estado === 'publicado' || ad.estado === 'programado') && (
                          <button
                            type="button"
                            className="btn-action btn-action--unpublish"
                            onClick={() => unpublishMutation.mutate(ad.id)}
                            disabled={unpublishMutation.isLoading}
                          >
                            Despublicar
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-action btn-action--delete"
                          onClick={() =>
                            setConfirmDialog({
                              title: 'Eliminar anuncio',
                              message: '¿Seguro que deseas eliminar este anuncio?',
                              onConfirm: () => {
                                deleteMutation.mutate(ad.id);
                                setConfirmDialog(null);
                              },
                            })
                          }
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingAd ? 'Editar anuncio' : 'Nuevo anuncio'}
      >
        <form onSubmit={handleSubmit} className="provider-ad-form">
          <div className="form-group">
            <label htmlFor="proveedor_name">Nombre comercial *</label>
            <input
              id="proveedor_name"
              type="text"
              value={formData.proveedor_name}
              onChange={(e) => setFormData({ ...formData, proveedor_name: e.target.value })}
              required
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label>Público objetivo *</label>
            {(formData.deportes_objetivo || []).map((slug, index) => (
              <div key={`publico-${index}`} className="provider-ad-publico-row">
                <select
                  aria-label={`Público objetivo ${index + 1}`}
                  value={slug}
                  onChange={(e) => {
                    const next = [...formData.deportes_objetivo];
                    next[index] = e.target.value;
                    setFormData({ ...formData, deportes_objetivo: next, deporte_objetivo: next[0] });
                  }}
                  required
                >
                  {DEPORTE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} disabled={formData.deportes_objetivo.includes(opt.value) && opt.value !== slug}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {(formData.deportes_objetivo || []).length > 1 && (
                  <button
                    type="button"
                    className="provider-ad-publico-remove"
                    onClick={() => {
                      const next = formData.deportes_objetivo.filter((_, itemIndex) => itemIndex !== index);
                      setFormData({ ...formData, deportes_objetivo: next, deporte_objetivo: next[0] });
                    }}
                  >
                    Quitar
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="provider-ad-publico-add"
              onClick={() => {
                const selected = new Set(formData.deportes_objetivo || []);
                const available = DEPORTE_OPTIONS.find((opt) => !selected.has(opt.value));
                if (!available) return;
                const next = [...(formData.deportes_objetivo || []), available.value];
                setFormData({ ...formData, deportes_objetivo: next, deporte_objetivo: next[0] });
              }}
              disabled={(formData.deportes_objetivo || []).length >= DEPORTE_OPTIONS.length}
            >
              Agregar deporte
            </button>
            <small>Puedes sumar varios deportes. El anuncio se muestra en cada uno.</small>
          </div>

          <div className="form-group">
            <label htmlFor="url_destino">Enlace de destino *</label>
            <input
              id="url_destino"
              type="url"
              value={formData.url_destino}
              onChange={(e) => setFormData({ ...formData, url_destino: e.target.value })}
              required
              placeholder="https://wa.me/..."
            />
          </div>

          <div className="form-group">
            <label>Visible desde *</label>
            <div className="provider-ad-fecha-row">
              <div className="provider-ad-fecha-field">
                <label htmlFor="fecha_inicio_date" className="provider-ad-fecha-sublabel">Fecha</label>
                <input
                  id="fecha_inicio_date"
                  type="date"
                  value={formData.fecha_inicio_date}
                  onChange={(e) => setFormData({ ...formData, fecha_inicio_date: e.target.value })}
                  required
                />
              </div>
              <div className="provider-ad-fecha-field">
                <label className="provider-ad-fecha-sublabel">Hora</label>
                <div className="provider-ad-time-select">
                  <select
                    id="fecha_inicio_hour"
                    aria-label="Hora de inicio"
                    value={parseTimeValue(formData.fecha_inicio_time).hour}
                    onChange={(e) => {
                      const { minute } = parseTimeValue(formData.fecha_inicio_time);
                      setFormData({
                        ...formData,
                        fecha_inicio_time: buildTimeValue(e.target.value, minute),
                      });
                    }}
                  >
                    {HOUR_OPTIONS.map((hour) => (
                      <option key={hour} value={hour}>
                        {hour} h
                      </option>
                    ))}
                  </select>
                  <span className="provider-ad-time-sep" aria-hidden="true">:</span>
                  <select
                    id="fecha_inicio_minute"
                    aria-label="Minutos de inicio"
                    value={parseTimeValue(formData.fecha_inicio_time).minute}
                    onChange={(e) => {
                      const { hour } = parseTimeValue(formData.fecha_inicio_time);
                      setFormData({
                        ...formData,
                        fecha_inicio_time: buildTimeValue(hour, e.target.value),
                      });
                    }}
                  >
                    {MINUTE_OPTIONS.map((minute) => (
                      <option key={minute} value={minute}>
                        {minute} min
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <small>Por defecto es hoy. Cámbiala si el anuncio debe salir más adelante.</small>
          </div>

          <div className="form-group">
            <label>Vigente hasta *</label>
            <div className="provider-ad-fecha-row">
              <div className="provider-ad-fecha-field">
                <label htmlFor="fecha_fin_date" className="provider-ad-fecha-sublabel">Fecha</label>
                <input
                  id="fecha_fin_date"
                  type="date"
                  value={formData.fecha_fin_date}
                  min={toDateInputValue(new Date().toISOString())}
                  onChange={(e) => setFormData({ ...formData, fecha_fin_date: e.target.value })}
                  required
                />
              </div>
              <div className="provider-ad-fecha-field">
                <label className="provider-ad-fecha-sublabel">Hora</label>
                <div className="provider-ad-time-select">
                  <select
                    id="fecha_fin_hour"
                    aria-label="Hora"
                    value={parseTimeValue(formData.fecha_fin_time).hour}
                    onChange={(e) => {
                      const { minute } = parseTimeValue(formData.fecha_fin_time);
                      setFormData({
                        ...formData,
                        fecha_fin_time: buildTimeValue(e.target.value, minute),
                      });
                    }}
                  >
                    {HOUR_OPTIONS.map((hour) => (
                      <option key={hour} value={hour}>
                        {hour} h
                      </option>
                    ))}
                  </select>
                  <span className="provider-ad-time-sep" aria-hidden="true">
                    :
                  </span>
                  <select
                    id="fecha_fin_minute"
                    aria-label="Minutos"
                    value={parseTimeValue(formData.fecha_fin_time).minute}
                    onChange={(e) => {
                      const { hour } = parseTimeValue(formData.fecha_fin_time);
                      setFormData({
                        ...formData,
                        fecha_fin_time: buildTimeValue(hour, e.target.value),
                      });
                    }}
                  >
                    {MINUTE_OPTIONS.map((minute) => (
                      <option key={minute} value={minute}>
                        {minute} min
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="provider-ad-time-preset"
                  onClick={() => setFormData({ ...formData, fecha_fin_time: '23:59' })}
                >
                  Usar fin del día (23:59)
                </button>
              </div>
            </div>
            <p className="form-hint">Por defecto 23:59 — fin del día seleccionado.</p>
          </div>

          <div className="form-group">
            <label>Banner (PNG, JPG o WEBP, máx. 3 MB) *</label>
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleUpload} />
            <p className="form-hint">
              Recomendado: {BANNER_RECOMMENDED_WIDTH}×{BANNER_RECOMMENDED_HEIGHT} px (proporción ~2:1).
              Mínimo: {BANNER_MIN_WIDTH}×{BANNER_MIN_HEIGHT} px.
            </p>
            {uploading && <p className="form-hint">Subiendo imagen...</p>}
            {formData.url_imagen && (
              <div className="banner-preview">
                <img src={formData.url_imagen} alt="Vista previa del banner" />
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={closeModal}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSaving || uploading}>
              {isSaving ? 'Guardando...' : editingAd ? 'Guardar cambios' : 'Guardar borrador'}
            </button>
          </div>
        </form>
      </Modal>

      {confirmDialog && (
        <ConfirmDialog
          isOpen
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onClose={() => setConfirmDialog(null)}
          type="danger"
        />
      )}
    </div>
  );
};

export default ProviderAds;
